// frontend/src/pages/InvoicePayPage.jsx
// Public invoice pay page: displays line items, total, then creates charge → redirects
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoiceAPI } from '../services/api';

const STATUS_LABELS = {
  draft:     { label:'Draft',     color:'#94a3b8' },
  sent:      { label:'Awaiting Payment', color:'#f59e0b' },
  paid:      { label:'Paid',      color:'#22c55e' },
  overdue:   { label:'Overdue',   color:'#f87171' },
  cancelled: { label:'Cancelled', color:'#475569' },
};

function fmt(n) { return Number(n || 0).toLocaleString(); }

export default function InvoicePayPage() {
  const { invoiceId } = useParams();
  const navigate      = useNavigate();
  const [data, setData]           = useState(null); // { invoice, merchant }
  const [loading, setLoading]     = useState(true);
  const [paying, setPaying]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    invoiceAPI.getPublic(invoiceId)
      .then(r => setData({ invoice: r.invoice, merchant: r.merchant }))
      .catch(() => setError('Invoice not found or unavailable.'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  async function handlePay() {
    setError('');
    setPaying(true);
    try {
      const r = await invoiceAPI.ensureCharge(invoiceId);
      navigate(r.payUrl);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open payment page. Try again.');
    } finally {
      setPaying(false);
    }
  }

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'#94a3b8' }}>Loading invoice...</div>
    </div>
  );

  if (error && !data) return (
    <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#1e293b', padding:32, borderRadius:12, color:'#f87171', textAlign:'center', maxWidth:360 }}>
        <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
        <div>{error}</div>
      </div>
    </div>
  );

  const { invoice, merchant } = data;
  const statusInfo = STATUS_LABELS[invoice.status] || STATUS_LABELS.sent;
  const isPaid = invoice.status === 'paid';
  const isCancelled = invoice.status === 'cancelled';
  const canPay = !isPaid && !isCancelled;

  return (
    <div style={{ minHeight:'100vh', background:'#0f172a', padding:16, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:40 }}>
      <div style={{ width:'100%', maxWidth:520 }}>
        {/* Header */}
        <div style={{ background:'#1e293b', borderRadius:16, padding:28, marginBottom:16, boxShadow:'0 4px 20px rgba(0,0,0,0.3)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
            <div>
              {merchant?.logoUrl
                ? <img src={merchant.logoUrl} alt="logo" style={{ height:40, objectFit:'contain', marginBottom:8 }} />
                : <div style={{ color:'#0ea5e9', fontWeight:800, fontSize:18, marginBottom:4 }}>{merchant?.businessName}</div>
              }
              <div style={{ color:'#94a3b8', fontSize:13 }}>Invoice {invoice.invoiceNumber || invoice.invoiceId}</div>
            </div>
            <span style={{ background: statusInfo.color + '22', color: statusInfo.color, padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600 }}>
              {statusInfo.label}
            </span>
          </div>

          {/* Customer */}
          {invoice.customerName && (
            <div style={{ marginBottom:16, padding:'12px 16px', background:'#0f172a', borderRadius:8 }}>
              <div style={{ color:'#64748b', fontSize:12 }}>Bill to</div>
              <div style={{ color:'#f1f5f9', fontWeight:600 }}>{invoice.customerName}</div>
              {invoice.customerPhone && <div style={{ color:'#94a3b8', fontSize:13 }}>{invoice.customerPhone}</div>}
            </div>
          )}

          {/* Due date */}
          {invoice.dueDate && (
            <div style={{ color:'#94a3b8', fontSize:13, marginBottom:16 }}>
              Due: <strong style={{ color:'#f1f5f9' }}>{new Date(invoice.dueDate).toLocaleDateString('en-UG', { dateStyle:'medium' })}</strong>
            </div>
          )}

          {/* Line items */}
          <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #334155' }}>
                <th style={{ textAlign:'left', color:'#64748b', fontSize:12, padding:'6px 0', fontWeight:600 }}>Description</th>
                <th style={{ textAlign:'right', color:'#64748b', fontSize:12, padding:'6px 0', fontWeight:600 }}>Qty</th>
                <th style={{ textAlign:'right', color:'#64748b', fontSize:12, padding:'6px 0', fontWeight:600 }}>Unit</th>
                <th style={{ textAlign:'right', color:'#64748b', fontSize:12, padding:'6px 0', fontWeight:600 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.map((li, i) => (
                <tr key={i} style={{ borderBottom:'1px solid #1e293b' }}>
                  <td style={{ color:'#f1f5f9', fontSize:14, padding:'10px 0' }}>{li.description}</td>
                  <td style={{ color:'#94a3b8', fontSize:14, textAlign:'right', padding:'10px 0' }}>{li.qty}</td>
                  <td style={{ color:'#94a3b8', fontSize:14, textAlign:'right', padding:'10px 0' }}>{fmt(li.unitPrice)}</td>
                  <td style={{ color:'#f1f5f9', fontSize:14, textAlign:'right', padding:'10px 0', fontWeight:600 }}>{fmt(li.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Total */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:12, borderTop:'1px solid #334155' }}>
            <span style={{ color:'#94a3b8', fontWeight:600, fontSize:15 }}>Total</span>
            <span style={{ color:'#0ea5e9', fontWeight:800, fontSize:22 }}>UGX {fmt(invoice.totalAmount)}</span>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div style={{ marginTop:16, padding:'12px 14px', background:'#0f172a', borderRadius:8, color:'#94a3b8', fontSize:13, lineHeight:1.5 }}>
              {invoice.notes}
            </div>
          )}
        </div>

        {/* Pay button */}
        {canPay && (
          <div style={{ background:'#1e293b', borderRadius:16, padding:24 }}>
            {error && <div style={{ color:'#f87171', fontSize:13, marginBottom:14, padding:'10px 14px', background:'rgba(239,68,68,0.1)', borderRadius:8 }}>{error}</div>}
            <button
              onClick={handlePay} disabled={paying}
              style={{ width:'100%', padding:16, background:paying ? '#374151':'#0ea5e9', color:'#fff', border:'none', borderRadius:12, fontSize:17, fontWeight:700, cursor:paying?'not-allowed':'pointer' }}
            >
              {paying ? 'Opening payment...' : `Pay UGX ${fmt(invoice.totalAmount)}`}
            </button>
            <div style={{ textAlign:'center', marginTop:12, color:'#475569', fontSize:12 }}>
              Secured by DWI FINTECH (U) LIMITED
            </div>
          </div>
        )}

        {isPaid && (
          <div style={{ background:'#052e16', border:'1px solid #166534', borderRadius:16, padding:24, textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:8 }}>✅</div>
            <div style={{ color:'#22c55e', fontWeight:700, fontSize:18 }}>Invoice Paid</div>
            {invoice.paidAt && <div style={{ color:'#94a3b8', fontSize:13, marginTop:8 }}>Paid on {new Date(invoice.paidAt).toLocaleDateString('en-UG', { dateStyle:'long' })}</div>}
          </div>
        )}

        {isCancelled && (
          <div style={{ background:'#1e293b', borderRadius:16, padding:24, textAlign:'center' }}>
            <div style={{ color:'#64748b', fontSize:16 }}>This invoice has been cancelled.</div>
          </div>
        )}
      </div>
    </div>
  );
}
